import React, { useEffect, useState } from "react";
import {
  Grid,
  FileText,
  Users,
  Box,
  Settings,
  LogOut,
  Plane,
  FolderKanban,
  Calendar,
  Camera,
  User,
  Radio,
  ChevronDown
} from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ThemeToggle } from "../ui/ThemeToggle";

const BASE_URL = "https://beigexmemehouse.s3.amazonaws.com/beige/";

const navItemsAdmin = [
  { path: "/dashboard", label: "Dashboard", Icon: Grid },
  { path: "/book-a-shoot", label: "Book A Shoot", Icon: FileText },
  { path: "/crew-management", label: "Crew Management", Icon: Users },
  {
    label: "Equipment",
    Icon: Box,
    children: [
      { path: "/equipment", label: "Equipment Management" },
      { path: "/rent-equipment-request", label: "Equipment Request" },
    ],
  },
  // { path: "/livestream", label: "Livestream", Icon: Radio },
];

const navItemsUser = [
  { path: "/creator-dashboard", label: "Dashboard", Icon: Grid },
  { path: "/crew-availability", label: "Availability", Icon: Calendar },
  { path: "/requests-and-shoots", label: "Requests & Shoots", Icon: Camera },
  { path: "/equipment-crew", label: "Equipment", Icon: Box },
  { path: "/profile", label: "Profile", Icon: User },
];

const comingSoonItems = [
  { label: "Travel", Icon: Plane },
  { label: "Manage All Bookings", Icon: FolderKanban },
];

const Navbar = ({ onClose, logout, user } = {}) => {
  const [navItems, setNavItems] = useState([]);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const [isEquipmentOpen, setIsEquipmentOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    if (user?.userTypeId === 1) {
      setNavItems(navItemsAdmin);
      setShowComingSoon(true);

      // Auto-open dropdown if we are on an equipment sub-page
      const isEquipmentPath = ["/equipment", "/rent-equipment-request"].includes(location.pathname);
      if (isEquipmentPath) {
        setIsEquipmentOpen(true);
      }
    } else if (user?.userTypeId === 2) {
      setNavItems(navItemsUser);
      setShowComingSoon(false);
    }

    const localStorageUser = localStorage.getItem("user");
    const userData = localStorageUser ? JSON.parse(localStorageUser) : user;

    if (userData?.crew_profile_image) {
      setProfileImage(`${BASE_URL}${userData.crew_profile_image}`);
    } else {
      setProfileImage("/images/misc/tempProfile.jpg");
    }
  }, [user, location.pathname]);

  const handleLinkClick = () => {
    if (typeof onClose === "function") onClose();
  };

  const logoutAndSetLightMode = () => {
    localStorage.setItem('theme', 'light');
    document.documentElement.classList.remove('dark');
    logout();
  };

  const NavItem = ({ item, isMobile = false }) => {
    const { path, label, Icon, children } = item;

    // Check if any child is currently active
    const isChildActive = children?.some(child => location.pathname === child.path);

    if (children) {
      return (
        <li key={label} className="w-full">
          <button
            onClick={() => setIsEquipmentOpen(!isEquipmentOpen)}
            className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium transition-all
              ${isChildActive
                ? "bg-button-coloured text-button-coloured-foreground shadow-sm"
                : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-zinc-800"
              }`}
          >
            <div className="flex items-center gap-3">
              <Icon className="w-5 h-5" />
              <span>{label}</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${isEquipmentOpen ? "rotate-180" : ""}`} />
          </button>

          {isEquipmentOpen && (
            <ul className="mt-1 ml-4 border-l-2 border-gray-100 dark:border-zinc-800 space-y-1">
              {children.map((child) => (
                <li key={child.path}>
                  <NavLink
                    to={child.path}
                    onClick={isMobile ? handleLinkClick : undefined}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-6 py-2 rounded-r-lg text-sm font-medium transition-all
                      ${isActive
                        ? "text-button-coloured font-bold dark:text-button-coloured"
                        : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                      }`
                    }
                  >
                    {child.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={path}>
        <NavLink
          to={path}
          onClick={isMobile ? handleLinkClick : undefined}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all
            ${isActive
              ? "bg-button-coloured text-button-coloured-foreground shadow-sm"
              : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-zinc-800"
            }`
          }
        >
          <Icon className="w-5 h-5" />
          {label}
        </NavLink>
      </li>
    );
  };

  return (
    <>
      {/* DESKTOP SIDEBAR */}
      <aside className="w-64 min-h-screen flex-col hidden md:flex border-r dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="px-6 py-6">
          <Link to="/dashboard">
            <img src="/images/logos/BEIGELOGO.PNG" alt="beige logo" />
          </Link>
        </div>

        <nav className="px-3 py-4 flex-1 overflow-y-auto no-scrollbar">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <NavItem key={item.label || item.path} item={item} />
            ))}
          </ul>

          <div className="my-4">
             <hr className="dark:border-zinc-800" />
          </div>

          {showComingSoon && (
            <div className="py-2">
              <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Coming soon</p>
              <ul className="space-y-1">
                {comingSoonItems.map(({ label, Icon }) => (
                  <li key={label} className="px-4 py-2 flex gap-3 text-sm text-gray-400 cursor-not-allowed">
                    <Icon className="w-5 h-5 opacity-70" />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </nav>

        <div className="border-t dark:border-zinc-800 px-4 py-3">
          <div className="flex justify-between items-center">
            <NavLink to="/settings" className="flex items-center gap-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Settings className="w-5 h-5" />
              Settings
            </NavLink>
            <ThemeToggle />
          </div>
        </div>

        <div className="px-4 py-4 border-t dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <img src={profileImage} className="w-10 h-10 rounded-full object-cover border dark:border-zinc-700" alt="avatar" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold dark:text-gray-200 truncate">{user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            </div>
            <button onClick={logoutAndSetLightMode} className="p-1 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* MOBILE CONTENT */}
      <div className="md:hidden w-full h-full flex flex-col overflow-hidden no-scrollbar">
        <div className="flex-1 min-h-0 overflow-y-auto">
          <nav className="p-4">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <NavItem key={item.label || item.path} item={item} isMobile={true} />
              ))}
            </ul>
          </nav>

          {showComingSoon && (
            <>
              <hr className="mx-4 dark:border-zinc-800" />
              <div className="p-4">
                <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Coming soon</p>
                <ul className="space-y-2">
                  {comingSoonItems.map(({ label, Icon }) => (
                    <li key={label} className="px-4 py-3 flex gap-3 text-sm text-gray-400">
                      <Icon className="w-5 h-5 opacity-70" />
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="px-4 py-4 border-t dark:border-zinc-800">
          <div className="flex items-center justify-between">
            <NavLink to="/settings" onClick={handleLinkClick} className="flex items-center gap-3 text-sm font-medium dark:text-gray-300">
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </NavLink>
            <ThemeToggle />
          </div>
        </div>

        <div className="px-4 py-4 border-t dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <img src={profileImage} className="w-10 h-10 rounded-full object-cover" alt="avatar" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold dark:text-gray-200 truncate">{user?.name || "Guest"}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            </div>
            <button onClick={logoutAndSetLightMode} className="p-2">
              <LogOut className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
